<!--
  =====================================================================
  CHI SIAMO PAGE - ABOUT US PAGE
  =====================================================================
  
  Company information and "About Us" page with dynamic sections and
  navigation submenu.
  
  FEATURES:
  - Dynamic section rendering with component type resolution
  - Auto-generated submenu from section titles
  - Anchor IDs for smooth scrolling to sections
  - Supports multiple section types (center, left-title, right-title, etc.)
  - Shows loading and error states
  
  =====================================================================
-->

<template>
  <div>
    <SiteHeader />
    
    <PageSubmenu
      v-if="state.page"
      :mainLabel="'Chi siamo'"
      :sections="state.pageSections"
      :labelKey="'title'"
      :overrideExisting="true"
    />

    <main class="pagina">
      <div v-if="state.loading">Caricamento...</div>
      
      <div v-else-if="state.error">Errore</div>
      
      <div v-else>
        <component
          v-for="(section, index) in state.pageSections"
          :key="index"
          :is="getComponent(section.type)"
          :id="slugify(section.content?.title || section.content?.eyebrow || '')"
          :content="section.content"
        />
      </div>
    </main>
    <ArrowUp /> 
    <SiteFooter />
  </div>
</template>

<script setup>
import { onMounted } from 'vue'
import { state, loadPageContent } from '@/store/index.js'

// ───────────────────────────────────────────────────────────────────
// SHARED LAYOUT COMPONENTS
// ───────────────────────────────────────────────────────────────────
import SiteHeader from '@/components/sections/shared/SiteHeader.vue'
import SiteFooter from '@/components/sections/shared/SiteFooter.vue'
import PageSubmenu from '@/components/sections/shared/PageSubmenu.vue'

// ───────────────────────────────────────────────────────────────────
// SECTION COMPONENTS
// ───────────────────────────────────────────────────────────────────
import SectionLeftTitleHtml from '@/components/sections/html/SectionLeftTitleHtml.vue'
import SectionDocumentHtml from '@/components/sections/html/SectionDocumentHtml.vue'

// ───────────────────────────────────────────────────────────────────
// UTILITIES
// ───────────────────────────────────────────────────────────────────
import ArrowUp from '@/components/ArrowUp.vue'

// ───────────────────────────────────────────────────────────────────
// LIFECYCLE HOOKS
// ───────────────────────────────────────────────────────────────────

/**
 * Load page content on component mount
 */
onMounted(() => {
  loadPageContent('/chi-siamo')
})

// ───────────────────────────────────────────────────────────────────
// UTILITIES FUNCTIONS
// ───────────────────────────────────────────────────────────────────

/**
 * Convert text to URL-safe anchor ID
 * - Removes diacritical marks
 * - Converts to lowercase
 * - Replaces spaces with hyphens
 * 
 * @param {string} str - Text to slugify
 * @returns {string} URL-safe slug
 */
function slugify(str = '') {
  return String(str)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * Maps section types to their corresponding Vue components
 * 
 * @param {string} type - The section type from page content
 * @returns {VueComponent} The component to render
 */
function getComponent(type) {
  switch (type) {
    case 'section-left-title-html': 
      return SectionLeftTitleHtml
    case 'section-document-html': 
      return SectionDocumentHtml
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/main';

.pagina {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  @include flex-gap(2rem, column);

  @media (max-width: 768px) {
    padding: 1rem;
  }
}
</style>